import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { getRoomsWithAvailability, getOrder, createReserve, patchOrder } from '../utils/api';
import { formatCurrency } from '../utils/formatters';
import { businessInfo } from '../config/info';
import RoomImageCarousel from '../components/RoomImageCarousel';
import { useAuth } from '../contexts/AuthContext';
import LoginSignupDialog from '../components/LoginSignupDialog';


const formatDateCol = (d) => d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
const toYMD = (d) => (d instanceof Date ? d.toISOString().slice(0, 10) : d);

const todayStr = () => new Date().toISOString().slice(0, 10);

/** Default check-in / check-out time (property policy). */
const DEFAULT_STAY_TIME = '10:00';

const pad2 = (n) => String(n).padStart(2, '0');

const ymdLocalFromDate = (d) => {
  const x = d instanceof Date ? d : new Date(d);
  if (isNaN(x.getTime())) return '';
  return `${x.getFullYear()}-${pad2(x.getMonth() + 1)}-${pad2(x.getDate())}`;
};

const hmLocalFromDate = (d) => {
  const x = d instanceof Date ? d : new Date(d);
  if (isNaN(x.getTime())) return DEFAULT_STAY_TIME;
  return `${pad2(x.getHours())}:${pad2(x.getMinutes())}`;
};

/** Local wall clock → ISO UTC for API (server accepts full ISO). */
const combineYmdHmToIso = (ymd, hm) => {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  let h = 10;
  let min = 0;
  if (hm && typeof hm === 'string') {
    const m = hm.trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (m) {
      h = Math.min(23, Math.max(0, parseInt(m[1], 10)));
      min = Math.min(59, Math.max(0, parseInt(m[2], 10)));
    }
  }
  const [y, mo, day] = ymd.split('-').map(Number);
  const dt = new Date(y, mo - 1, day, h, min, 0, 0);
  if (isNaN(dt.getTime())) return null;
  return dt.toISOString();
};

/** Add one day to a YYYY-MM-DD string. Pure date math, no time. */
const addOneDayYMD = (ymd) => {
  const [y, m, d] = ymd.split('-').map(Number);
  const next = new Date(Date.UTC(y, m - 1, d + 1));
  return next.toISOString().slice(0, 10);
};

/** YYYY-MM-DD strings from startYMD (inclusive) to endYMD (exclusive). Pure dates only, no time. */
const getYMDsBetween = (startYMD, endYMD) => {
  const out = [];
  let current = startYMD;
  while (current < endYMD) {
    out.push(current);
    current = addOneDayYMD(current);
  }
  return out;
};

const ymdFromDob = (val) => {
  if (val == null || val === '') return '';
  if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) return val.slice(0, 10);
  try {
    const x = new Date(val);
    if (isNaN(x.getTime())) return '';
    return x.toISOString().slice(0, 10);
  } catch {
    return '';
  }
};

const guestDobValid = (dob) => /^\d{4}-\d{2}-\d{2}$/.test((dob || '').trim());

const BookPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const orderIdFromUrl = searchParams.get('orderId');
  const { isLoggedIn, user } = useAuth();
  const [loginOpen, setLoginOpen] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setOrderLoading] = useState(false);
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [loadedOrder, setLoadedOrder] = useState(null);
  const appliedLoadedOrderRef = useRef(false);
  const [startDate, setStartDate] = useState(todayStr());
  const [carouselRoom, setCarouselRoom] = useState(null);
  const [checkinDateYMD, setCheckinDateYMD] = useState('');
  const [checkoutDateYMD, setCheckoutDateYMD] = useState('');
  const [checkinTime, setCheckinTime] = useState(DEFAULT_STAY_TIME);
  const [checkoutTime, setCheckoutTime] = useState(DEFAULT_STAY_TIME);
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  const [guests, setGuests] = useState([{ name: '', phone: '', email: '', dob: '' }]);


  useEffect(() => {
    if (!isLoggedIn || !orderIdFromUrl) {
      setLoadedOrder(null);
      setEditingOrderId(null);
      appliedLoadedOrderRef.current = false;
      return;
    }
    setOrderLoading(true);
    getOrder(orderIdFromUrl)
      .then((res) => {
        const order = res.data;
        if (order && order.status !== 'confirmed') {
          setLoadedOrder(order);
          setEditingOrderId(order._id);
          const checkinYMD = toYMD(order.checkinDate);
          if (checkinYMD) setStartDate(checkinYMD);
        } else {
          setLoadedOrder(null);
          setEditingOrderId(null);
        }
      })
      .catch(() => {
        setLoadedOrder(null);
        setEditingOrderId(null);
      })
      .finally(() => setOrderLoading(false));
  }, [isLoggedIn, orderIdFromUrl]);

  useEffect(() => {
    if (!loadedOrder || !rooms.length || appliedLoadedOrderRef.current) return;
    const roomIdStr = (loadedOrder.roomId?._id || loadedOrder.roomId)?.toString();
    if (!roomIdStr) return;
    const checkin = new Date(loadedOrder.checkinDate);
    const checkout = new Date(loadedOrder.checkoutDate);
    if (isNaN(checkin.getTime()) || isNaN(checkout.getTime()) || checkout <= checkin) return;
    setCheckinDateYMD(ymdLocalFromDate(checkin));
    setCheckoutDateYMD(ymdLocalFromDate(checkout));
    setCheckinTime(hmLocalFromDate(checkin));
    setCheckoutTime(hmLocalFromDate(checkout));
    setSelectedRoomId(roomIdStr);
    if (Array.isArray(loadedOrder.guestDetails) && loadedOrder.guestDetails.length > 0) {
      setGuests(
        loadedOrder.guestDetails.map((g) => ({
          name: g.name || '',
          phone: g.phone || '',
          email: g.email || '',
          dob: ymdFromDob(g.dob),
        }))
      );
    }
    appliedLoadedOrderRef.current = true;
  }, [loadedOrder, rooms]);

  useEffect(() => {
    if (!isLoggedIn) {
      setRooms([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    getRoomsWithAvailability()
      .then((res) => {
        const { rooms: roomList = [], orders = [] } = res.data || {};
        const unavailableByRoom = {};
        for (const order of orders) {
          const rid = order.roomId != null ? String(order.roomId) : null;
          if (!rid) continue;
          const cinRaw = order.checkinDate;
          const coutRaw = order.checkoutDate;
          const cinYMD = typeof cinRaw === 'string' ? cinRaw.slice(0, 10) : (cinRaw && typeof cinRaw === 'object' && cinRaw.toISOString ? new Date(cinRaw).toISOString().slice(0, 10) : null);
          const coutYMD = typeof coutRaw === 'string' ? coutRaw.slice(0, 10) : (coutRaw && typeof coutRaw === 'object' && coutRaw.toISOString ? new Date(coutRaw).toISOString().slice(0, 10) : null);
          if (!cinYMD || !coutYMD || !/^\d{4}-\d{2}-\d{2}$/.test(cinYMD) || !/^\d{4}-\d{2}-\d{2}$/.test(coutYMD) || cinYMD >= coutYMD) continue;
          if (!unavailableByRoom[rid]) unavailableByRoom[rid] = {};
          for (const ymd of getYMDsBetween(cinYMD, coutYMD)) {
            unavailableByRoom[rid][ymd] = true;
          }
        }
        const roomsWithUnavailable = (Array.isArray(roomList) ? roomList : []).map((room) => {
          const rid = (room._id || room.id) != null ? String(room._id || room.id) : '';
          const unavailableDates = Object.keys(unavailableByRoom[rid] || {}).sort();
          return { ...room, unavailableDates };
        });
        setRooms(roomsWithUnavailable);
      })
      .catch(() => setRooms([]))
      .finally(() => setLoading(false));
  }, [isLoggedIn]);

  const dateColumns = useMemo(() => {
    if (checkinDateYMD && checkoutDateYMD && checkinDateYMD < checkoutDateYMD) {
      return getYMDsBetween(checkinDateYMD, checkoutDateYMD);
    }
    const start = startDate || todayStr();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(start)) return [];
    let end = start;
    for (let i = 0; i < 7; i++) end = addOneDayYMD(end);
    return getYMDsBetween(start, end);
  }, [checkinDateYMD, checkoutDateYMD, startDate]);

  const selected = useMemo(() => {
    if (!checkinDateYMD || !checkoutDateYMD || !selectedRoomId || checkinDateYMD >= checkoutDateYMD) return [];
    const room = rooms.find((r) => (r._id || r.id).toString() === selectedRoomId);
    if (!room) return [];
    const dateStrings = getYMDsBetween(checkinDateYMD, checkoutDateYMD);
    return dateStrings.map((date) => ({
      roomId: selectedRoomId,
      roomName: room.name || '',
      rent: room.rent || 0,
      date,
    }));
  }, [checkinDateYMD, checkoutDateYMD, selectedRoomId, rooms]);

  const isUnavailable = (room, dateStr) => {
    const list = room.unavailableDates || [];
    return list.includes(dateStr);
  };

  const isSelected = (roomId, dateStr) => {
    const idStr = (roomId || '').toString();
    return selected.some((s) => s.roomId === idStr && s.date === dateStr);
  };

  const selectedRoom = rooms.find((r) => (r._id || r.id).toString() === selectedRoomId) || null;
  const roomCapacity = selectedRoom?.capacity ?? 0;
  const maxGuests = roomCapacity > 0 ? roomCapacity : 10;
  const canAddGuest = guests.length < maxGuests;

  const addGuest = () => {
    if (!canAddGuest) return;
    setGuests((prev) => [...prev, { name: '', phone: '', email: '', dob: '' }]);
  };

  const addMeAsGuest = () => {
    if (!user) return;
    const me = {
      name: (user.name || '').trim(),
      phone: (user.phone || '').trim(),
      email: (user.email || '').trim(),
      dob: ymdFromDob(user.dob),
    };
    setGuests((prev) => {
      const firstEmpty = prev.findIndex(
        (g) => !(g.name?.trim() && g.phone?.trim() && guestDobValid(g.dob))
      );
      if (firstEmpty === -1) {
        if (prev.length >= maxGuests) return prev;
        return [...prev, me];
      }
      const next = prev.slice();
      next[firstEmpty] = { ...next[firstEmpty], ...me };
      return next;
    });
  };

  const removeGuest = (index) => {
    if (guests.length <= 1) return;
    setGuests((prev) => prev.filter((_, i) => i !== index));
  };

  const setGuestField = (index, field, value) => {
    setGuests((prev) =>
      prev.map((g, i) => (i === index ? { ...g, [field]: value } : g))
    );
  };

  const summary = useMemo(() => {
    if (selected.length === 0) return null;
    const dates = selected.map((s) => s.date).sort();
    const minDate = dates[0];
    const maxDate = dates[dates.length - 1];
    const totalNights = selected.length;
    const totalCost = selected.reduce((sum, s) => sum + (s.rent || 0), 0);
    return { minDate, maxDate, totalNights, totalCost, lines: selected };
  }, [selected]);

  const hasUnavailableInRange = useMemo(() => {
    if (!selectedRoom || selected.length === 0) return false;
    const unavailable = selectedRoom.unavailableDates || [];
    return selected.some((s) => unavailable.includes(s.date));
  }, [selectedRoom, selected]);

  const datesValid =
    Boolean(
      checkinDateYMD &&
        checkoutDateYMD &&
        /^\d{4}-\d{2}-\d{2}$/.test(checkinDateYMD) &&
        /^\d{4}-\d{2}-\d{2}$/.test(checkoutDateYMD) &&
        checkinDateYMD < checkoutDateYMD
    );
  const guestsValid =
    guests.length >= 1 &&
    guests.every(
      (g) =>
        (g.name || '').trim() &&
        (g.phone || '').trim() &&
        guestDobValid(g.dob)
    ) &&
    guests.length <= maxGuests;

  const canRequest =
    summary &&
    selected.length > 0 &&
    !hasUnavailableInRange &&
    datesValid &&
    guestsValid;

  const [reserveSaving, setReserveSaving] = useState(false);
  const [showBookingErrors, setShowBookingErrors] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [successOrderId, setSuccessOrderId] = useState(null);
  const [successIsUpdate, setSuccessIsUpdate] = useState(false);

  const guestDetailsPayload = () =>
    guests.map((g) => ({
      name: (g.name || '').trim(),
      phone: (g.phone || '').trim(),
      email: (g.email || '').trim(),
      dob: (g.dob || '').trim().slice(0, 10),
    }));

  const handleReserve = async () => {
    if (!canRequest || !summary || !selectedRoomId || !selectedRoom) return;
    if (hasUnavailableInRange) return;
    const checkinDate = combineYmdHmToIso(summary.minDate, checkinTime);
    const checkoutDate = combineYmdHmToIso(checkoutDateYMD, checkoutTime);
    if (!checkinDate || !checkoutDate) return;
    setReserveSaving(true);
    try {
      const { data } = await createReserve({
        roomId: selectedRoomId,
        roomName: selectedRoom.name,
        checkinDate,
        checkoutDate,
        guestDetails: guestDetailsPayload(),
      });
      const order = data.order;
      if (order?._id) {
        setSearchParams({ orderId: order._id });
        setEditingOrderId(order._id);
        setLoadedOrder(order);
        setSuccessOrderId(order.orderId || order._id);
        setSuccessIsUpdate(false);
        setSuccessDialogOpen(true);
      }
    } catch (err) {
      console.error('Reserve failed:', err);
    } finally {
      setReserveSaving(false);
    }
  };

  const handleSave = async () => {
    if (!editingOrderId || !summary || !selectedRoomId || !selectedRoom) return;
    if (hasUnavailableInRange) return;
    const checkinDate = combineYmdHmToIso(summary.minDate, checkinTime);
    const checkoutDate = combineYmdHmToIso(checkoutDateYMD, checkoutTime);
    if (!checkinDate || !checkoutDate) return;
    setReserveSaving(true);
    try {
      const { data } = await patchOrder(editingOrderId, {
        roomId: selectedRoomId,
        roomName: selectedRoom.name,
        checkinDate,
        checkoutDate,
        guestDetails: guestDetailsPayload(),
      });
      setLoadedOrder((prev) =>
        prev && prev._id === editingOrderId
          ? { ...prev, checkinDate, checkoutDate, roomId: selectedRoomId, roomName: selectedRoom.name }
          : prev
      );
      const order = data?.order;
      setSuccessOrderId(order?.orderId || order?._id || editingOrderId);
      setSuccessIsUpdate(true);
      setSuccessDialogOpen(true);
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setReserveSaving(false);
    }
  };

  const onReserveClick = () => {
    if (!canRequest) {
      setShowBookingErrors(true);
      return;
    }
    setShowBookingErrors(false);
    handleReserve();
  };

  const onSaveClick = () => {
    if (!canRequest) {
      setShowBookingErrors(true);
      return;
    }
    setShowBookingErrors(false);
    handleSave();
  };

  const dateRangeInvalid =
    Boolean(checkinDateYMD && checkoutDateYMD && checkinDateYMD >= checkoutDateYMD);

  return (
    <Box sx={{ pt: 10, pb: 8, px: 3, minHeight: '100vh', bgcolor: 'var(--color-cream)' }}>
      {!isLoggedIn ? (
        <Box sx={{ maxWidth: 500, mx: 'auto', textAlign: 'center', py: 8 }}>
          <Typography
            variant="h5"
            sx={{
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontWeight: 500,
              mb: 2,
              color: 'var(--color-charcoal)',
            }}
          >
            Login to view room availability
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Sign in or create an account to check dates and request a reservation.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => setLoginOpen(true)}
            sx={{
              bgcolor: 'var(--color-accent)',
              '&:hover': { bgcolor: 'var(--color-charcoal)' },
            }}
          >
            Login
          </Button>
          <LoginSignupDialog
            open={loginOpen}
            onClose={() => setLoginOpen(false)}
            onSuccess={() => setLoginOpen(false)}
          />
        </Box>
      ) : (
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        <Box sx={{ mb: 4, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField
            label="Start date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText="7 days shown"
            sx={{ minWidth: 160, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
          />
        </Box>

        <Typography
          variant="h4"
          sx={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontWeight: 500,
            mb: 1,
            color: 'var(--color-charcoal)',
          }}
        >
          Room Availability
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Your request is for checking only; the booking is not confirmed until the hotel confirms it.
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: 'var(--color-accent)' }} />
          </Box>
        ) : rooms.length === 0 ? (
          <Typography color="text.secondary">
            No rooms available. <Link to="/">Return home</Link>.
          </Typography>
        ) : (
          <>
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{ border: '1px solid var(--color-sand)', borderRadius: 2, overflow: 'auto' }}
            >
              <Table size="small" sx={{ minWidth: 600 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'var(--color-sand)' }}>
                    <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>Room</TableCell>
                    {dateColumns.map((ymd) => (
                      <TableCell
                        key={ymd}
                        align="center"
                        sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
                      >
                        {formatDateCol(new Date(ymd + 'T12:00:00Z'))}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rooms.map((room) => (
                    <TableRow key={room._id} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box
                            onClick={() =>
                              (room.imageUrl || room.imageUrls?.length) && setCarouselRoom(room)
                            }
                            sx={{
                              width: 56,
                              height: 56,
                              borderRadius: 1,
                              bgcolor: 'var(--color-sand)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              overflow: 'hidden',
                              cursor:
                                room.imageUrl || room.imageUrls?.length ? 'pointer' : 'default',
                              transition: 'opacity 0.2s',
                              '&:hover':
                                room.imageUrl || room.imageUrls?.length
                                  ? { opacity: 0.9 }
                                  : {},
                            }}
                          >
                            {room.imageUrl ? (
                              <Box
                                component="img"
                                src={room.imageUrl}
                                alt={room.name}
                                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            ) : (
                              <Typography sx={{ fontSize: '1.5rem', opacity: 0.5 }}>🛏️</Typography>
                            )}
                          </Box>
                          <Box>
                            <Typography fontWeight={600}>{room.name}</Typography>
                            {room.capacity > 0 && (
                              <Typography variant="caption" color="text.secondary">
                                Up to {room.capacity} guests
                              </Typography>
                            )}
                            {room.rent > 0 && (
                              <Typography
                                variant="body2"
                                sx={{ color: 'var(--color-accent)', fontWeight: 500 }}
                              >
                                {formatCurrency(room.rent)}/night
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      {dateColumns.map((ymd) => {
                        const dateStr = ymd;
                        const unavailable = isUnavailable(room, dateStr);
                        const selectedCell = isSelected(room._id, dateStr);
                        const isOtherRoom = selectedRoomId != null && (room._id || room.id).toString() !== selectedRoomId;
                        return (
                          <TableCell
                            key={ymd}
                            align="center"
                            sx={{
                              bgcolor: selectedCell
                                ? 'var(--color-accent)'
                                : unavailable || isOtherRoom
                                  ? 'rgba(0,0,0,0.06)'
                                  : 'transparent',
                              color: selectedCell ? 'white' : 'inherit',
                              opacity: isOtherRoom ? 0.65 : 1,
                            }}
                          >
                            {unavailable ? (
                              <Chip
                                label="Unavailable"
                                size="small"
                                sx={{
                                  borderRadius: 1,
                                  bgcolor: 'rgba(0,0,0,0.12)',
                                  color: 'text.secondary',
                                }}
                              />
                            ) : isOtherRoom ? (
                              <Typography variant="caption" color="text.secondary">—</Typography>
                            ) : selectedCell ? (
                              <Chip
                                label="Selected"
                                size="small"
                                sx={{
                                  borderRadius: 1,
                                  bgcolor: 'rgba(255,255,255,0.3)',
                                  color: 'white',
                                  fontWeight: 600,
                                }}
                              />
                            ) : (
                              <Chip
                                label="Available"
                                size="small"
                                color="success"
                                variant="outlined"
                                sx={{ borderRadius: 1 }}
                              />
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <RoomImageCarousel
              room={carouselRoom}
              open={!!carouselRoom}
              onClose={() => setCarouselRoom(null)}
            />

            <Box
              sx={{
                mt: 3,
                p: 2,
                borderRadius: 2,
                border: '1px solid var(--color-sand)',
                bgcolor: 'white',
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                alignItems: 'flex-start',
              }}
            >
              <Typography sx={{ fontWeight: 600, width: '100%', mb: 0.5 }}>Booking details</Typography>
              <TextField
                label="Check-in date"
                type="date"
                value={checkinDateYMD}
                onChange={(e) => setCheckinDateYMD(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                required
                error={showBookingErrors && (!checkinDateYMD || dateRangeInvalid)}
                helperText={
                  showBookingErrors && !checkinDateYMD
                    ? 'Required'
                    : showBookingErrors && dateRangeInvalid
                      ? 'Must be before check-out'
                      : undefined
                }
                sx={{ minWidth: 180, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                inputProps={{ min: todayStr() }}
              />
              <TextField
                label="Check-out date"
                type="date"
                value={checkoutDateYMD}
                onChange={(e) => setCheckoutDateYMD(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                required
                error={showBookingErrors && (!checkoutDateYMD || dateRangeInvalid)}
                helperText={
                  showBookingErrors && !checkoutDateYMD
                    ? 'Required'
                    : showBookingErrors && dateRangeInvalid
                      ? 'Must be after check-in'
                      : undefined
                }
                sx={{ minWidth: 180, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                inputProps={{ min: checkinDateYMD || todayStr() }}
              />
              <TextField
                label="Check-in time"
                type="time"
                value={checkinTime}
                onChange={(e) => setCheckinTime(e.target.value || DEFAULT_STAY_TIME)}
                InputLabelProps={{ shrink: true }}
                size="small"
                inputProps={{ step: 60 }}
                helperText="Default 10:00"
                sx={{ minWidth: 140, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
              />
              <TextField
                label="Check-out time"
                type="time"
                value={checkoutTime}
                onChange={(e) => setCheckoutTime(e.target.value || DEFAULT_STAY_TIME)}
                InputLabelProps={{ shrink: true }}
                size="small"
                inputProps={{ step: 60 }}
                helperText="Default 10:00"
                sx={{ minWidth: 140, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
              />
              <FormControl
                size="small"
                sx={{ minWidth: 220 }}
                variant="outlined"
                error={showBookingErrors && !selectedRoomId}
                required
              >
                <InputLabel id="book-room-label">Room</InputLabel>
                <Select
                  labelId="book-room-label"
                  label="Room"
                  value={selectedRoomId || ''}
                  onChange={(e) => setSelectedRoomId(e.target.value || null)}
                  sx={{ borderRadius: 1 }}
                >
                  <MenuItem value="">Select room</MenuItem>
                  {rooms.map((room) => (
                    <MenuItem key={room._id} value={room._id}>
                      {room.name}
                      {room.rent > 0 ? ` — ${formatCurrency(room.rent)}/night` : ''}
                      {room.capacity > 0 ? ` · ${room.capacity} guests` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                size="small"
                onClick={() => {
                  setCheckinDateYMD('');
                  setCheckoutDateYMD('');
                  setCheckinTime(DEFAULT_STAY_TIME);
                  setCheckoutTime(DEFAULT_STAY_TIME);
                  setSelectedRoomId(null);
                  setGuests([{ name: '', phone: '', email: '', dob: '' }]);
                }}
                sx={{ textTransform: 'none', alignSelf: 'center' }}
              >
                Clear selection
              </Button>
            </Box>

            {summary && hasUnavailableInRange && (
              <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                Some selected dates are unavailable for this room. Please choose different check-in or check-out dates.
              </Typography>
            )}

            {summary && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid var(--color-sand)',
                  bgcolor: 'white',
                }}
              >
                <Typography sx={{ fontWeight: 600, mb: 1 }}>Selected room & dates</Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 1 }}>
                  {summary.lines.map((s, i) => (
                    <li key={`${s.roomId}-${s.date}-${i}`}>
                      {s.roomName} — {s.date} ({formatCurrency(s.rent)})
                    </li>
                  ))}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Check-in: {summary.minDate} {checkinTime} · Check-out: {checkoutDateYMD} {checkoutTime} · Total
                  room-nights: {summary.totalNights} · Total: {formatCurrency(summary.totalCost)}
                </Typography>
                {selectedRoom && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Room capacity: {maxGuests} guest{maxGuests !== 1 ? 's' : ''} max
                  </Typography>
                )}
              </Box>
            )}

            {selectedRoomId && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid var(--color-sand)',
                  bgcolor: 'white',
                }}
              >
                <Typography sx={{ fontWeight: 600, mb: 2 }}>Guests</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Each guest needs name, phone, and date of birth. Add up to {maxGuests} guest{maxGuests !== 1 ? 's' : ''}{' '}
                  (room capacity).
                </Typography>
                {showBookingErrors && !guestsValid && (
                  <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                    Enter every guest&apos;s name, phone, and date of birth, or remove empty guest rows.
                  </Typography>
                )}
                {guests.map((guest, index) => (
                  <Paper
                    key={index}
                    variant="outlined"
                    sx={{ p: 2, mb: 2, borderRadius: 2, borderColor: 'var(--color-sand)' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle2">Guest {index + 1}</Typography>
                      {guests.length > 1 && (
                        <IconButton size="small" onClick={() => removeGuest(index)} aria-label="Remove guest" color="error">
                          <DeleteOutlineIcon />
                        </IconButton>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField
                        label="Name"
                        value={guest.name}
                        onChange={(e) => setGuestField(index, 'name', e.target.value)}
                        fullWidth
                        size="small"
                        required
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                      />
                      <TextField
                        label="Phone"
                        value={guest.phone}
                        onChange={(e) => setGuestField(index, 'phone', e.target.value)}
                        fullWidth
                        size="small"
                        required
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                      />
                      <TextField
                        label="Email"
                        type="email"
                        value={guest.email}
                        onChange={(e) => setGuestField(index, 'email', e.target.value)}
                        fullWidth
                        size="small"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                      />
                      <TextField
                        label="Date of birth"
                        type="date"
                        value={guest.dob || ''}
                        onChange={(e) => setGuestField(index, 'dob', e.target.value)}
                        fullWidth
                        size="small"
                        required
                        InputLabelProps={{ shrink: true }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                      />
                    </Box>
                  </Paper>
                ))}
                {canAddGuest && (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      startIcon={<AddIcon />}
                      onClick={addGuest}
                      variant="outlined"
                      size="small"
                      sx={{ borderRadius: 2 }}
                    >
                      Add guest
                    </Button>
                    {user && (user.name || user.phone || user.email) && (
                      <Button
                        onClick={addMeAsGuest}
                        variant="outlined"
                        size="small"
                        sx={{ borderRadius: 2 }}
                      >
                        Add me
                      </Button>
                    )}
                  </Box>
                )}
              </Box>
            )}

            <Box
              sx={{
                mt: 4,
                p: 2,
                borderRadius: 2,
                border: '1px solid var(--color-sand)',
                bgcolor: 'white',
              }}
            >
              <Typography sx={{ fontWeight: 600, mb: 1.5 }}>Terms and conditions</Typography>
              <Box component="ol" sx={{ m: 0, pl: 2.5, listStyleType: 'lower-alpha', '& li': { mb: 0.75 } }}>
                <li>
                  <Typography variant="body2" color="text.secondary">
                    You are making a booking with the hotel directly.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" color="text.secondary">
                    Please review the booking and cancelation policies for the bookings. In case you make a change or cancel the booking, the cancelation penalties specified may apply.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" color="text.secondary">
                    You may be asked to furnish the form of payment and identification proofs during check-in.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" color="text.secondary">
                    Other inclusions not listed as a part of this booking may be chargeable.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" color="text.secondary">
                    If the booking requires payment by a certain date, non payment by that date will cause the booking to be automatically canceled.
                  </Typography>
                </li>
              </Box>
            </Box>

            {showBookingErrors && !canRequest && (
              <Typography variant="body2" color="error" sx={{ mt: 3 }}>
                Select a room, set check-in and check-out (at least one night), ensure dates are available, and add at least one
                guest with name, phone, and date of birth.
              </Typography>
            )}

            <Box sx={{ mt: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {editingOrderId ? (
                <Button
                  onClick={onSaveClick}
                  disabled={reserveSaving}
                  variant="contained"
                  sx={{
                    px: 3,
                    py: 1.5,
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: 2,
                    bgcolor: 'var(--color-accent)',
                    '&:hover': { bgcolor: 'var(--color-accent-light)' },
                  }}
                >
                  {reserveSaving ? 'Saving...' : 'Save'}
                </Button>
              ) : (
                <Button
                  onClick={onReserveClick}
                  disabled={reserveSaving}
                  variant="contained"
                  sx={{
                    px: 3,
                    py: 1.5,
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: 2,
                    bgcolor: 'var(--color-accent)',
                    '&:hover': { bgcolor: 'var(--color-accent-light)' },
                  }}
                >
                  {reserveSaving ? 'Requesting...' : 'Request reservation'}
                </Button>
              )}
              <Button component={Link} to="/" variant="outlined" sx={{ borderRadius: 2 }}>
                Back to Home
              </Button>
            </Box>
          </>
        )}
      </Box>
      )}

      <Dialog
        open={successDialogOpen}
        onClose={() => setSuccessDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 600, color: 'var(--color-charcoal)' }}>
          Success
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {successIsUpdate
              ? 'Your booking has been updated successfully.'
              : 'Your reservation request has been sent. The hotel will confirm after checking availability.'}
          </Typography>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
            Order ID
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontFamily: 'monospace',
              fontWeight: 700,
              letterSpacing: 0.5,
              mb: 2,
              color: 'var(--color-accent)',
            }}
          >
            {successOrderId || '—'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Please pay to the UPI ID below and send the payment screenshot to{' '}
            <strong>{businessInfo.email}</strong> with your <strong>Order ID ({successOrderId || '—'})</strong> in the subject or message.
          </Typography>
          {businessInfo.upiId ? (
            <Typography
              variant="body1"
              sx={{
                fontFamily: 'monospace',
                fontWeight: 600,
                py: 1.5,
                px: 2,
                bgcolor: 'var(--color-sand)',
                borderRadius: 1,
                color: 'var(--color-charcoal)',
              }}
            >
              {businessInfo.upiId}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              UPI ID will be shared by the hotel. Please mention your Order ID when you pay or send the screenshot.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setSuccessDialogOpen(false);
              navigate('/account');
            }}
            variant="contained"
            sx={{
              borderRadius: 2,
              bgcolor: 'var(--color-accent)',
              '&:hover': { bgcolor: 'var(--color-accent-light)' },
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BookPage;
